import { Component, OnInit, inject } from "@angular/core";
import { provideTranslocoScope, TranslocoService } from "@jsverse/transloco";
import { Pages } from "../../../common/constants/pages/Pages";
import { TabService } from "../../../services/tabs/TabService";
import { ReportDataService } from "../services/ReportDataService";
import { ReportItem } from "../shared/ReportItem";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import {
    GenericListField,
    GenericListFieldType,
} from "@app/shared/models/GenericList/GenericListField";
import { firstValueFrom } from "rxjs";

@Component({
    selector: "app-report-overview",
    templateUrl: "./report-overview.component.html",
    styleUrls: ["./report-overview.component.scss"],
    standalone: false,
    providers: [provideTranslocoScope("report")],
})
export class ReportOverviewComponent implements OnInit {
    tabService = inject(TabService);
    pages = inject(Pages);
    private _reportDS = inject(ReportDataService);
    private _promptService = inject(CerrixPromptService);
    private _transloco = inject(TranslocoService);

    data: ReportItem[];
    allModules: Record<string, string>;

    protected finishedLoading = false;

    async ngOnInit() {
        // download() builds its dialog labels with the synchronous translate(). Load the lazy
        // scope JSON first, otherwise those labels resolve to the raw key.
        await firstValueFrom(this._transloco.load(`report/${this._transloco.getActiveLang()}`));

        this.allModules = await firstValueFrom(this._reportDS.getModules());
        this.data = await firstValueFrom(this._reportDS.getList());

        this.finishedLoading = true;
    }

    getCategories() {
        const modules = this.allModules;
        const items = this.data.map(function (a) {
            if (modules) {
                return modules[a.Module];
            }
        });
        return new Set(items);
    }

    getCategoryReports(c: string): ReportItem[] {
        const modules = this.allModules;
        const items = this.data.filter(function (a) {
            if (modules) {
                return modules[a.Module] === c;
            }
        });
        return items;
    }

    open(report: ReportItem) {
        this.tabService.generateTab(this.pages.PowerBIReport, report.ID, report.Name);
    }

    download(report: ReportItem) {
        this._promptService
            .prompt({
                maxHeight: 400,
                data: {
                    confirmOnEnter: false,
                    closeOnEsc: false,
                    title: this._transloco.translate("report.exportFilterTitle"),
                    fields: [
                        {
                            prettyName: this._transloco.translate("report.filterLabel"),
                            fieldName: "filter",
                            fieldType: GenericListFieldType.TextArea,
                            customValidation: (row) => {
                                const isInvalid = row.filter && row.filter.length > 2000;
                                return {
                                    makeRequired: isInvalid,
                                    validation: isInvalid
                                        ? this._transloco.translate("report.filterMaxLengthError")
                                        : "",
                                };
                            },
                        },
                    ],
                    confirmButton: {
                        text: this._transloco.translate("report.okButton"),
                    },
                    cancelButton: {
                        show: true,
                    },
                },
            })
            .getResult()
            .subscribe((result) => {
                if (result) {
                    let filter = result.filter.replace(/\n/g, " ");
                    this._reportDS.export(report.ID, filter);
                }
            });
    }
}
